import type { StaticImageData } from 'next/image';
import type { ReactNode } from 'react';
import classNames from 'classnames';
import { defineMessages } from 'react-intl';
import Image from '@alltrails/core/components/Image';
import Typography from '@alltrails/core/components/Typography';
import { useFormatMessage } from '@alltrails/core/hooks/useFormatMessage';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import AppIcon from '@alltrails/denali/components/AppIcon';
import { Modal } from '@alltrails/modal';
import AppStore from '../Footer/StoreBadges/AppStore';
import GooglePlay from '../Footer/StoreBadges/GooglePlay';
import styles from './styles.module.scss';

const ALT_TEXT_STRINGS = defineMessages({
  QR_CODE_ALT_TEXT: {
    defaultMessage: 'A QR code that links to the AllTrails mobile app store listing'
  },
  SUBTITLE_MOBILE: {
    defaultMessage: 'Available now for iOS or Android.'
  },
  SUBTITLE_DESKTOP: {
    defaultMessage: 'Scan the QR code to download the app.'
  }
});

type Props = {
  className?: string;
  isOpen: boolean;
  onRequestClose: () => void;
  qrCodeImgSrc: string | StaticImageData;
  subtitleDesktop?: ReactNode;
  subtitleMobile?: ReactNode;
  title: ReactNode;
  variant?: 'navigator' | 'explore';
};

const MobileFeatureModal = ({
  className,
  isOpen,
  onRequestClose,
  qrCodeImgSrc,
  subtitleDesktop,
  subtitleMobile,
  title,
  variant = 'navigator'
}: Props) => {
  const languageRegionCode = useLanguageRegionCode();

  const {
    intl,
    formattedDefaultMessages: { QR_CODE_ALT_TEXT, SUBTITLE_MOBILE, SUBTITLE_DESKTOP }
  } = useFormatMessage(ALT_TEXT_STRINGS);
  const headingVariant = variant === 'navigator' ? 'heading500' : 'heading400';
  const subtitleColor = variant === 'navigator' ? undefined : 'secondary';
  const subtitleVariant = variant === 'navigator' ? 'paragraph100' : 'text200';

  return (
    <Modal isOpen={isOpen} onCloseRequest={onRequestClose}>
      <div className={classNames(styles.container, className)}>
        <Image alt={QR_CODE_ALT_TEXT} className={styles.qrCode} height={240} src={qrCodeImgSrc} unoptimized width={240} />
        <AppIcon className={styles.appIcon} size={135} />
        <Typography className={styles.title} component="h2" variant={headingVariant}>
          {title}
        </Typography>
        <Typography color={subtitleColor} component="p" variant={subtitleVariant}>
          {subtitleMobile || SUBTITLE_MOBILE}
          <span className={styles.desktopText}> {subtitleDesktop || SUBTITLE_DESKTOP}</span>
        </Typography>
        <div className={styles.mobileStoreButtons}>
          <AppStore height={40} intl={intl} languageRegionCode={languageRegionCode} />
          <GooglePlay height={40} intl={intl} languageRegionCode={languageRegionCode} />
        </div>
      </div>
    </Modal>
  );
};

export default MobileFeatureModal;
